import React from 'react';
import bemBlock from '@gik/core/utils/bemBlock';
import InkindSearch from '../InkindSearch';
import Accordion, { AccordionItem } from '@gik/ui/Accordion';
import { Button, FacebookIcon, InstagramIcon, PinterestIcon, TwitterIcon, YouTubeIcon } from '@gik/ui';
import Link from '@/components/Link';
import Logo from '@gik/core/assets/img/icons/giveinkind-logo.svg';
import SvgIcon from '@gik/ui/SvgIcon';
import { navigateTo, logout } from '@/utils/environment';
import Avatar from '@gik/ui/Avatar';
import { useTranslation } from 'react-i18next';
import { translationKeys } from '@gik/i18n/en/app';
import routes from '@gik/core/routes';
import { useUserStore } from '@gik/core/store/UserStore';
import { useUser } from '@gik/api/users/user';
import PageRowsSection from '../PageRows/PageRowsSection';
import { auth } from '@gik/auth';

export interface IAppNavMobileProps extends React.HTMLAttributes<HTMLDivElement> {
  searchRef?: React.Ref<HTMLInputElement>;
}

const socialMediaLinks = [
  <FacebookIcon key="facebook" href={'https://www.facebook.com/giveinkind/'} />,
  <TwitterIcon key="twitter" href={'https://twitter.com/giveinkind'} />,
  <InstagramIcon key="instagram" href={'https://www.instagram.com/giveinkind/'} />,
  <PinterestIcon key="pinterest" href={'https://www.pinterest.com/giveinkind'} />,
  <YouTubeIcon key="youtube" href={'https://www.youtube.com/channel/UCGp15GUbx5bFwxQwAQUpnRw'} />,
];

export default function AppNavMobile({ className, searchRef }: IAppNavMobileProps): React.ReactElement {
  const bem = bemBlock('app-nav-mobile');
  const { t } = useTranslation();

  const userStore = useUserStore();
  const userId = userStore.id;
  const { data: user } = useUser(userId);

  return (
    <div className={bem(null, null, className)}>
      <div className={bem('logo')}>
        <Link href={routes.userProfile}>
          <a>
            <Logo />
          </a>
        </Link>
      </div>
      {userId && (
        <div className={bem('btn-home')}>
          <Link href={routes.userProfile}>
            <Button circle variant="secondary">
              <SvgIcon name="HomeOutline" size="base" />
            </Button>
          </Link>
        </div>
      )}
      {!userId && (
        <div className={bem('btn-login')}>
          <a onClick={() => auth.login()}>{t(translationKeys.signin)}</a>
        </div>
      )}
      <ul className={bem('nav')}>
        <li>
          <InkindSearch pill ref={searchRef} />
        </li>
      </ul>
      {userId && <PageRowsSection />}
      {userId && <hr className={bem('pages-divider')} />}
      {userId && (
        <ul className={bem('avatar')}>
          <li>
            <Link href={routes.userProfile}>
              <a>
                <Avatar loading={!user} imageUrl={user?.avatarThumbSmallURL} />
                <span>{t(translationKeys.yourProfileAndPages)}</span>
              </a>
            </Link>
          </li>
        </ul>
      )}
      <ul className={bem('marketing-links')}>
        <li className={bem('start-page')}>
          <Link href={routes.create}>
            <Button block={false} wide variant="secondary" size="lg" pill uppercase>
              {t(translationKeys.startAPage)}
            </Button>
          </Link>
        </li>
        <li>
          <Accordion dividers={false}>
            <AccordionItem header={t(translationKeys.whyGik)}>
              <ul className={bem('subnav')}>
                <li>
                  <Link href={routes.howItWork}>
                    <a>{t(translationKeys.howItWorks)}</a>
                  </Link>
                </li>
                <li>
                  <Link href={routes.features}>
                    <a>{t(translationKeys.features)}</a>
                  </Link>
                </li>
                <li>
                  <Link href={routes.tipsAndSamplePages}>
                    <a>{t(translationKeys.tipsAndSamplePages)}</a>
                  </Link>
                </li>
                {/* <li>
                  <Link href={routes.competitors}>
                    <a>{t(translationKeys.comparisonChart)}</a>
                  </Link>
                </li>
                <li>
                  <Link href={routes.benefits}>
                    <a>{t(translationKeys.benefits)}</a>
                  </Link>
                </li> */}
                <li>
                  <Link href={routes.commonQuestions}>
                    <a>{t(translationKeys.commonQuestions)}</a>
                  </Link>
                </li>
              </ul>
            </AccordionItem>
            <AccordionItem header={t(translationKeys.waysToHelp)}>
              <ul className={bem('subnav')}>
                <li>
                  <Link href={routes.giveBySituation}>
                    <a>{t(translationKeys.bySituation)}</a>
                  </Link>
                </li>
                <li>
                  <Link href={routes.byGiftType}>
                    <a>{t(translationKeys.byGiftType)}</a>
                  </Link>
                </li>
                <li>
                  <Link href={routes.blog}>
                    <a>{t(translationKeys.blog)}</a>
                  </Link>
                </li>
                <li>
                  <Link href={routes.giftGuides}>
                    <a>{t(translationKeys.giftGuides)}</a>
                  </Link>
                </li>
              </ul>
            </AccordionItem>
            <AccordionItem header={t(translationKeys.aboutGik)} onClick={() => navigateTo(routes.about)} />
            <AccordionItem header={t(translationKeys.contact)} onClick={() => navigateTo(routes.contact)} />
            <AccordionItem header={t(translationKeys.support)} onClick={() => navigateTo(routes.support)} />
            {userId && (
              <AccordionItem
                header={<span className="tw-text-danger-500">{t(translationKeys.logout)}</span>}
                onClick={logout}
              />
            )}
            {!userId && <AccordionItem header={t(translationKeys.signIn)} onClick={() => auth.login()} />}
          </Accordion>
        </li>
      </ul>
      <div className={bem('connect')}>
        <p>{t(translationKeys.connect)}</p>
      </div>
      <div className={bem('social-links')}>{socialMediaLinks}</div>
    </div>
  );
}
